import { useEffect, useState, useRef, useCallback, memo } from 'react'
import { Loader2, ZoomIn, ZoomOut, Scan, Search, ChevronUp, ChevronDown, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { apiService } from '@/service/api'
import { PdfJsViewer } from '@/components/PdfViewer/PdfJsViewer'
import { MarkdownViewer } from '@/components/PdfViewer/MarkdownViewer'
import AdBlockerWarning from '@/components/AdBlockerWarning'
import { processPageText } from '@/utils/pageTextUtils'
import { documentLoadQueue } from '@/utils/documentLoadQueue'
import { usePreventWheelPropagation } from '@/hooks/usePreventWheelPropagation'
import { useZoom } from '@/hooks/useZoom'
import { ZoomReadout } from '@/components/intueri/ZoomReadout'
import PreviewMatchList from '@/components/PreviewSearch/PreviewMatchList'
import { useImageMatches } from '@/components/PreviewSearch/useImageMatches'

interface DocumentPreviewProps {
  docId: string
  showTranscript?: boolean
  goToPage?: number
  width?: number
  height?: number
  /**
   * Open already searching for this text: a PDF jumps to the first page that
   * has it and highlights every hit, a drawing image opens its find box on it.
   */
  initialSearch?: string
}

// Global cache for document URLs with reference counting to prevent memory leaks
interface CachedUrl {
  url: string
  refCount: number
  fileType: 'pdf' | 'image'
}
const documentUrlCache = new Map<string, CachedUrl>()

// Global in-flight request tracker to prevent duplicate downloads
const inFlightDownloads = new Map<string, Promise<{ url: string; fileType: 'pdf' | 'image' }>>()

/**
 * Revoke all blob URLs that have no active component references (refCount 0)
 * and clear the caches. Called when navigating between boards to prevent
 * orphaned blob URLs from leaking memory.
 */
export function clearBlobCache(): void {
  for (const [key, entry] of documentUrlCache) {
    if (entry.refCount <= 0) {
      URL.revokeObjectURL(entry.url)
      documentUrlCache.delete(key)
    }
  }
  // Cancel any queued downloads that haven't started yet
  inFlightDownloads.clear()
}

/**
 * Store a blob URL in documentUrlCache so PdfPreview can reuse it later.
 * Called by useThumbnail's Strategy 2 to avoid duplicate downloads.
 */
export function cacheDocumentBlob(docId: string, blobUrl: string, fileType: 'pdf' | 'image'): void {
  const cacheKey = `${docId}-pdf`
  if (documentUrlCache.has(cacheKey)) return
  documentUrlCache.set(cacheKey, { url: blobUrl, refCount: 0, fileType })
}

/**
 * Get a preloaded blob URL for a document (for thumbnail rendering).
 * Returns immediately if cached, waits for in-flight download if pending, or null.
 */
export async function getPreloadedBlobUrl(docId: string): Promise<string | null> {
  const cacheKey = `${docId}-pdf`
  const cached = documentUrlCache.get(cacheKey)
  if (cached) return cached.url

  const inFlight = inFlightDownloads.get(cacheKey)
  if (inFlight) {
    try {
      const result = await inFlight
      return result.url
    } catch {
      return null
    }
  }

  return null
}

/**
 * Preload document blobs so they're cached before preview shapes mount.
 * Called at board load time to eliminate the mount → download waterfall.
 */
export function preloadDocumentBlobs(docIds: string[]): void {
  for (const docId of docIds) {
    const cacheKey = `${docId}-pdf`
    // Skip if already cached or already downloading
    if (documentUrlCache.has(cacheKey) || inFlightDownloads.has(cacheKey)) continue

    const promise = documentLoadQueue.add(cacheKey, async () => {
      const existingEntry = documentUrlCache.get(cacheKey)
      if (existingEntry) {
        return { url: existingEntry.url, fileType: existingEntry.fileType }
      }

      const blob = await apiService.downloadDocument(docId)
      const blobUrl = URL.createObjectURL(blob)
      const detectedType: 'pdf' | 'image' = blob.type.startsWith('image/')
        ? 'image'
        : 'pdf'
      // refCount 0 — component mount will increment when it reads from cache
      documentUrlCache.set(cacheKey, { url: blobUrl, refCount: 0, fileType: detectedType })
      return { url: blobUrl, fileType: detectedType }
    })

    inFlightDownloads.set(cacheKey, promise)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing catch param type
    promise.catch((err: any) => {
      // Suppress 404s for graph documents that have no backing file.
      // Log other unexpected errors so they remain observable.
      if (err?.response?.status !== 404) {
        console.warn('[preloadDocumentBlobs] download error', cacheKey, err)
      }
    }).finally(() => { inFlightDownloads.delete(cacheKey) })
  }
}

// Image preview with zoom controls and fit-to-screen
const ImagePreviewWithControls = ({
  src,
  docId,
  width,
  height,
  initialSearch,
}: {
  src: string
  docId?: string
  width?: number
  height?: number
  initialSearch?: string
}) => {
  const { t } = useTranslation('board')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const [scale, setScale] = useState(1)
  const [isFitToScreen, setIsFitToScreen] = useState(true)
  // FS-657: find in image. The indexed word positions come from the single-page
  // PDF the image was converted to at indexing time, so they map onto the
  // rendered image by one ratio (see useImageMatches).
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  // Seed the find box the way typing would, as PdfJsViewer's `initialSearch`
  // does, so a seeded search and a typed one cannot diverge.
  useEffect(() => {
    const seed = initialSearch?.trim()
    if (!seed) return
    setIsSearchOpen(true)
    setQuery(seed)
  }, [initialSearch])
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null)
  const { matches, boxes, pageWidth, pageHeight, isSearching } = useImageMatches(docId, query)
  // Baseline scale: the fit-to-screen scale (the view the preview opens at), so
  // 100% == the opening view. Must be state (not a ref) so the readout repaints.
  const [baseScale, setBaseScale] = useState<number>(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const wheelCallbackRef = usePreventWheelPropagation()

  const calculateFitScale = useCallback(() => {
    if (!containerRef.current || !imgRef.current) return 1
    const { clientWidth: cw, clientHeight: ch } = containerRef.current
    const { naturalWidth: nw, naturalHeight: nh } = imgRef.current
    if (!nw || !nh || !cw || !ch) return 1
    const padding = 40
    return Math.min((cw - padding) / nw, (ch - padding) / nh, 1)
  }, [])

  // FS-562: 100% == the fit-to-SCREEN scale — i.e. the view the preview opens
  // at. Calibrating the baseline to the opening scale (rather than fit-to-width)
  // means the readout opens at exactly 100% and +/- step a clean 10% (a tall or
  // small image used to open off-grid, e.g. 67%, so the first click jumped oddly).
  const updateBaseScale = useCallback(() => {
    const fitScale = calculateFitScale()
    if (fitScale > 0) setBaseScale(fitScale)
  }, [calculateFitScale])

  const handleFitToScreen = useCallback(() => {
    const fitScale = calculateFitScale()
    setScale(fitScale)
    setPosition({ x: 0, y: 0 })
    setIsFitToScreen(true)
  }, [calculateFitScale])

  // FS-562: 100% == the fit-to-screen baseline; +/- and type-in step a clean 10%
  // in percent space and clamp to the 25–400% raster policy. (Was a tangle of
  // absolute scale clamps mixed with baseScale-relative percents — for a large
  // image in a small card the floor `(0.1/baseScale)×100` exceeded 100%, so
  // zoom-out couldn't go below 100% and zoom-in jumped unpredictably.)
  const { percent: zoomPercent, zoomIn, zoomOut, setPercent } = useZoom({
    model: 'percent',
    scale,
    baseline: baseScale,
    onApply: (s) => {
      setScale(s)
      if (s <= baseScale) setPosition({ x: 0, y: 0 })
      setIsFitToScreen(false)
    },
  })

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const container = containerRef.current
    const img = imgRef.current
    if (!container || !img) return
    const overflowsW = img.naturalWidth * scale > container.clientWidth
    const overflowsH = img.naturalHeight * scale > container.clientHeight
    if (!overflowsW && !overflowsH) return
    e.preventDefault()
    setIsDragging(true)
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y })
  }, [scale, position])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return
    setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y })
  }, [isDragging, dragStart])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Apply fit-to-screen on image load and compute baseline
  const handleImageLoad = useCallback(() => {
    updateBaseScale()
    if (imgRef.current) {
      setNaturalSize({ w: imgRef.current.naturalWidth, h: imgRef.current.naturalHeight })
    }
    if (isFitToScreen) {
      const fitScale = calculateFitScale()
      setScale(fitScale)
    }
  }, [isFitToScreen, calculateFitScale, updateBaseScale])

  // Reset on the QUERY, not the match count: "pump" (3 hits) → "valve" (3 hits)
  // keeps the count identical, so keying on it left the new search opening on its
  // last match ("3 of 3").
  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  // The match list shrinks the pan area by its own width, and fit-to-screen is
  // otherwise only computed on image load — so opening search on a fitted image
  // left the old, now-too-large scale in place and clipped the drawing.
  const isListDocked = isSearchOpen && !!query.trim()
  useEffect(() => {
    if (!isFitToScreen) return
    const fitScale = calculateFitScale()
    if (fitScale > 0) {
      setScale(fitScale)
      setBaseScale(fitScale)
      setPosition({ x: 0, y: 0 })
    }
  }, [isListDocked, isFitToScreen, calculateFitScale])

  // Cmd+F / Ctrl+F while the pointer is inside this preview.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!((e.metaKey || e.ctrlKey) && e.key === 'f')) return
      const target = e.target
      if (!(target instanceof Node)) return
      if (!containerRef.current?.contains(target)) return
      e.preventDefault()
      setIsSearchOpen(true)
      setTimeout(() => searchInputRef.current?.focus(), 0)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false)
    setQuery('')
    setActiveIndex(0)
  }, [])

  const stepMatch = useCallback(
    (delta: number) => {
      if (matches.length === 0) return
      setActiveIndex((i) => (i + delta + matches.length) % matches.length)
    },
    [matches.length]
  )

  // PDF points → image pixels. The converted page is exactly the image (no
  // letterboxing) so there's no offset — but the axes need separate ratios:
  // convert_image_to_pdf derives width_pt and height_pt from independent x/y DPI
  // (document_index.py:1014), so a non-square-DPI scan's page aspect differs
  // from its pixel aspect and a single ratio drifts the boxes down the image.
  const boxScaleX = naturalSize && pageWidth ? naturalSize.w / pageWidth : 0
  const boxScaleY = naturalSize && pageHeight ? naturalSize.h / pageHeight : 0

  // Merge refs for wheel prevention
  const setRefs = useCallback((node: HTMLDivElement | null) => {
    containerRef.current = node
    wheelCallbackRef(node)
  }, [wheelCallbackRef])

  return (
    <div
      className="flex bg-gray-50 dark:bg-dark-muted overflow-hidden"
      style={{
        width: width ? `${width}px` : '100%',
        height: height ? `${height}px` : '100%',
      }}
    >
      {/* Pan/zoom area. containerRef stays on this rather than the outer box so
          fit-to-screen measures the space the image actually gets, excluding the
          match list. */}
      <div
        ref={setRefs}
        // tabIndex: focusable so the board can move focus in here when the preview
        // opens — Cmd+F is scoped to keydowns inside this element, so without it
        // the browser's find-in-page claimed the key instead.
        tabIndex={0}
        className="relative flex-1 min-w-0 h-full overflow-hidden focus:outline-none"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
      {isSearchOpen && (
        <div
          className="absolute top-4 left-4 z-20 flex items-center gap-2 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-lg px-2 py-1.5"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <Search className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" aria-hidden />
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                stepMatch(e.shiftKey ? -1 : 1)
              }
              if (e.key === 'Escape') closeSearch()
            }}
            placeholder={t('pdf_viewer.tools.search_placeholder')}
            aria-label={t('pdf_viewer.tools.search_placeholder')}
            className="w-40 px-1 py-0.5 text-sm bg-transparent focus:outline-none"
          />
          {query.trim() && !isSearching && (
            <span className="text-xs text-gray-500 dark:text-dark-text-muted whitespace-nowrap">
              {matches.length > 0
                ? `${activeIndex + 1} of ${matches.length}`
                : t('preview_search.no_matches')}
            </span>
          )}
          <button
            type="button"
            onClick={() => stepMatch(-1)}
            disabled={matches.length === 0}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-dark-accent disabled:opacity-50"
            aria-label={t('search.previous_match_title')}
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => stepMatch(1)}
            disabled={matches.length === 0}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-dark-accent disabled:opacity-50"
            aria-label={t('search.next_match_title')}
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={closeSearch}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-dark-accent"
            aria-label={t('search.close')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {/* Floating toolbar */}
      <div
        className="absolute top-4 right-4 flex gap-1 bg-gray-900/90 p-1 rounded-lg shadow-lg z-10"
        onPointerDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={zoomOut}
          className="p-2 text-white hover:bg-gray-700 rounded-lg transition-colors"
          aria-label={t('pdf_viewer.zoom.zoom_out')}
        >
          <ZoomOut className="w-4 h-4" />
        </button>
        <ZoomReadout
          percent={zoomPercent}
          onSetPercent={setPercent}
          label={t('common:zoom.level', { defaultValue: 'Zoom level' })}
          className="px-2 py-1 text-white text-xs hover:bg-gray-700 rounded-lg transition-colors min-w-[48px] text-center tabular-nums cursor-text"
          inputClassName="px-2 py-1 text-white text-xs bg-gray-800 rounded-lg min-w-[48px] text-center tabular-nums outline-none focus:ring-2 focus:ring-blue-400"
        />
        <button
          onClick={zoomIn}
          className="p-2 text-white hover:bg-gray-700 rounded-lg transition-colors"
          aria-label={t('pdf_viewer.zoom.zoom_in')}
        >
          <ZoomIn className="w-4 h-4" />
        </button>
        <button
          onClick={handleFitToScreen}
          className={`p-2 rounded-lg transition-colors ${
            isFitToScreen ? 'bg-blue-500 text-white' : 'text-white hover:bg-gray-700'
          }`}
          aria-label={t('pdf_viewer.zoom.fit_to_screen')}
        >
          <Scan className="w-4 h-4" />
        </button>
        {/* Cmd+F only fires when the keydown target is inside this preview, and
            neither the image nor the pan container is focusable — so straight
            after opening a card the shortcut has nothing to match and search
            was unreachable. A button also makes it discoverable, like the PDF
            viewer's. */}
        <button
          onClick={() => {
            setIsSearchOpen(true)
            setTimeout(() => searchInputRef.current?.focus(), 0)
          }}
          className={`p-2 rounded-lg transition-colors ${
            isSearchOpen ? 'bg-blue-500 text-white' : 'text-white hover:bg-gray-700'
          }`}
          // String(): the typed key resolver collides with String.prototype.search
          // for this key — same wrap PdfJsViewer uses.
          aria-label={String(t('pdf_viewer.tools.search'))}
        >
          <Search className="w-4 h-4" />
        </button>
      </div>

      {/* Image */}
      <div className="w-full h-full flex items-center justify-center">
        {/* The transform lives on this wrapper rather than the <img> so the
            search overlay shares the same zoom and pan. */}
        <div
          className="relative"
          style={{
            transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            transition: isDragging ? 'none' : 'transform 200ms',
          }}
        >
          <img
            ref={imgRef}
            src={src}
            alt={t('board:document_preview.image_alt', { defaultValue: 'Document preview' })}
            className="select-none"
            style={{
              display: 'block',
              cursor: (containerRef.current && imgRef.current && (imgRef.current.naturalWidth * scale > containerRef.current.clientWidth || imgRef.current.naturalHeight * scale > containerRef.current.clientHeight)) ? (isDragging ? 'grabbing' : 'grab') : 'default',
              maxWidth: 'none',
              maxHeight: 'none',
            }}
            draggable={false}
            onLoad={handleImageLoad}
          />
          {isSearchOpen && boxScaleX > 0 && boxScaleY > 0 && (
            <div className="absolute inset-0 pointer-events-none" data-testid="image-search-overlay">
              {boxes.map((match, matchIdx) =>
                match.boxes.map((box, boxIdx) => (
                  <div
                    key={`hl-${matchIdx}-${boxIdx}`}
                    style={{
                      position: 'absolute',
                      left: box.x0 * boxScaleX,
                      top: box.y0 * boxScaleY,
                      width: (box.x1 - box.x0) * boxScaleX,
                      height: (box.y1 - box.y0) * boxScaleY,
                      backgroundColor:
                        matchIdx === activeIndex ? 'rgba(255, 150, 0, 0.5)' : 'rgba(255, 235, 0, 0.4)',
                      borderRadius: '2px',
                    }}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </div>
      </div>
      {isSearchOpen && query.trim() && (
        <PreviewMatchList
          matches={matches}
          activeIndex={activeIndex}
          query={query}
          onSelect={setActiveIndex}
          isSearching={isSearching}
          className="w-[264px] shrink-0 border-l border-gray-200 dark:border-dark-border"
        />
      )}
    </div>
  )
}

const DocumentPreview = ({ docId, showTranscript = false, goToPage, width, height, initialSearch }: DocumentPreviewProps) => {
  const { t } = useTranslation('board')
  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  const [fileType, setFileType] = useState<'pdf' | 'image'>('pdf')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(goToPage ?? 1)
  const [hasCompatibilityIssues, setHasCompatibilityIssues] = useState(false)
  const [pageText, setPageText] = useState<string | null>(null)
  const [loadingText, setLoadingText] = useState(true)
  // Track whether this component instance has incremented the ref count
  const hasIncrementedRef = useRef(false)

  // Navigate to page when goToPage prop changes
  useEffect(() => {
    if (goToPage !== undefined && goToPage !== currentPage) {
      setCurrentPage(goToPage)
    }
  }, [goToPage])

  useEffect(() => {
    let isMounted = true
    hasIncrementedRef.current = false

    const fetchDocument = async () => {
      if (!docId) return

      // Check cache first
      const cacheKey = `${docId}-pdf` // docId is already globally unique
      const cachedEntry = documentUrlCache.get(cacheKey)

      if (cachedEntry) {
        // Only increment reference count if still mounted to avoid leaked refs
        if (!isMounted) return
        cachedEntry.refCount++
        hasIncrementedRef.current = true
        setDocumentUrl(cachedEntry.url)
        setFileType(cachedEntry.fileType)
        setLoading(false)
        return
      }

      // Check if there's already an in-flight download for this document
      let inFlightPromise = inFlightDownloads.get(cacheKey)
      const isCreator = !inFlightPromise
      
      if (isCreator) {
        // Create new in-flight download
        inFlightPromise = documentLoadQueue.add(cacheKey, async () => {
          // Double-check cache in case another request completed while we were queued
          const existingEntry = documentUrlCache.get(cacheKey)
          if (existingEntry) {
            existingEntry.refCount++
            return { url: existingEntry.url, fileType: existingEntry.fileType }
          }

          const blob = await apiService.downloadDocument(docId)
          const blobUrl = URL.createObjectURL(blob)
          // Detect file type from blob MIME type
          const detectedType: 'pdf' | 'image' = blob.type.startsWith('image/')
            ? 'image'
            : 'pdf'
          // Cache the URL with initial reference count of 1
          documentUrlCache.set(cacheKey, { url: blobUrl, refCount: 1, fileType: detectedType })
          return { url: blobUrl, fileType: detectedType }
        })

        // Track the in-flight download
        inFlightDownloads.set(cacheKey, inFlightPromise)

        // Clean up in-flight download when complete (success or failure)
        inFlightPromise.finally(() => {
          inFlightDownloads.delete(cacheKey)
        })
      }

      if (!inFlightPromise) {
        // This should never happen, but handle it gracefully
        if (isMounted) {
          setError(t('board:document_preview.failed_to_initiate_download', { defaultValue: 'Failed to initiate document download' }))
          setLoading(false)
        }
        return
      }

      if (isMounted) {
        setLoading(true)
        setError(null)
      }
      
      try {
        const result = await inFlightPromise

        // Only increment ref count if we joined an existing download (not if we created it)
        // The creator already has refCount: 1 from the cache initialization
        if (isMounted) {
          const entry = documentUrlCache.get(cacheKey)
          if (entry) {
            if (isCreator) {
              // We created the download, refCount is already 1 for us
              hasIncrementedRef.current = true
            } else {
              // We joined an existing download, need to increment
              entry.refCount++
              hasIncrementedRef.current = true
            }
          }
          setDocumentUrl(result.url)
          setFileType(result.fileType)
        }
      } catch (err) {
        if (isMounted) {
          setError(t('board:document_preview.failed_to_load_document', { defaultValue: 'Failed to load document' }))
          console.error('Failed to load document:', err)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchDocument()
    
    return () => {
      isMounted = false
    }
  }, [docId])

  // Load page text only when transcript is shown for better performance
  useEffect(() => {
    const loadPageText = async () => {
      if (!docId || !showTranscript) {
        setPageText(null)
        setLoadingText(false)
        return
      }

      setLoadingText(true)
      try {
        const textContent = await apiService.getDocumentPageText(
          docId,
          currentPage - 1
        )
        const processedText = processPageText(textContent, currentPage, true)
        setPageText(processedText)
      } catch (error) {
        console.error('Error loading page text:', error)
        setPageText(null)
      } finally {
        setLoadingText(false)
      }
    }

    loadPageText()
  }, [docId, currentPage, showTranscript])

  // Handle page synchronization (same as in PdfViewer.tsx)
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
  }


  // Cleanup effect for object URL with reference counting
  useEffect(() => {
    return () => {
      // Only decrement if this component actually incremented the ref count
      if (!docId || !hasIncrementedRef.current) return
      
      const cacheKey = `${docId}-pdf` // docId is already globally unique
      const entry = documentUrlCache.get(cacheKey)
      if (!entry) return
      
      // Decrement reference count
      entry.refCount = Math.max(0, entry.refCount - 1)
      
      // If no more references, revoke the URL and remove from cache
      if (entry.refCount === 0) {
        URL.revokeObjectURL(entry.url)
        documentUrlCache.delete(cacheKey)
      }
    }
  }, [docId])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400 dark:text-gray-500" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-4">
        <div className="text-red-500 dark:text-red-400 text-center mb-4">{error}</div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          {t('board:document_preview.reload', { defaultValue: 'Reload' })}
        </button>
      </div>
    )
  }

  // Only show transcript for PDF files (images don't have transcripts)
  const canShowTranscript = fileType === 'pdf' && showTranscript

  return (
    <div className="h-full flex flex-col">
      {fileType === 'pdf' && (
        <AdBlockerWarning
          onDetectionComplete={setHasCompatibilityIssues}
          showToast={false}
          showInline={hasCompatibilityIssues}
        />
      )}
      {documentUrl ? (
        <div className="flex h-full min-h-0">
          {/* Document Viewer - PDF or Image */}
          <div className={canShowTranscript ? 'flex-1' : 'w-full'}>
            <div className="h-full overflow-hidden">
              {fileType === 'pdf' ? (
                <PdfJsViewer
                  url={documentUrl}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onTotalPagesChange={() => {}}
                  documentId={docId}
                  initialSearch={initialSearch}
                />
              ) : (
                <ImagePreviewWithControls
                  src={documentUrl}
                  docId={docId}
                  width={width}
                  height={height}
                  initialSearch={initialSearch}
                />
              )}
            </div>
          </div>

          {/* Transcript Content - only for PDF files */}
          {canShowTranscript && (
            <div className="flex-1 border rounded-lg shadow-lg h-full overflow-hidden">
              {loadingText ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-gray-500 dark:text-dark-text-muted">{t('board:document_preview.loading_transcript', { defaultValue: 'Loading transcript...' })}</div>
                </div>
              ) : (
                <MarkdownViewer
                  content={pageText}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onTotalPagesChange={() => {}}
                  isPageContent={true}
                />
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="h-full flex flex-col items-center justify-center">
          <div className="text-gray-500 dark:text-dark-text-muted text-center mb-4">
            {t('board:document_preview.preview_not_available', { defaultValue: 'Document preview not available' })}
          </div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            {t('board:document_preview.reload', { defaultValue: 'Reload' })}
          </button>
        </div>
      )}
    </div>
  )
}

export default memo(DocumentPreview)
